import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LongPressCallback, LongPressHandlers, LongPressOptions } from 'use-long-press';
import { useLongPress as useBaseLongPress } from 'use-long-press';

const BLOCKED_EVENTS = ['click', 'mouseup', 'touchend'] as const;
const BLOCKED_EVENTS_DURATION_MS = 100;

type UseLongPressReturn = {
  longPressOccurredRef: React.MutableRefObject<boolean>;
  longPressProps: LongPressHandlers | undefined;
};

/**
 * Hook for handling long press gestures with event blocking to prevent conflicts.
 * Blocks click events after a long press to prevent unwanted interactions.
 *
 * @param callback - Function to call when long press is detected. Pass null to disable.
 * @param options - Optional configuration for the long press behavior.
 * @returns Object containing longPressProps to spread on the target element and a ref tracking if long press occurred.
 */
export function useLongPress(
  callback: (() => void) | null,
  options?: LongPressOptions,
): UseLongPressReturn {
  // ref for synchronous access, state to trigger useEffect
  const longPressOccurredRef = useRef(false);
  const [shouldBlockEvents, setShouldBlockEvents] = useState(false);

  const resetFlags = useCallback(() => {
    longPressOccurredRef.current = false;
    setShouldBlockEvents(false);
  }, []);

  const setBlockingFlags = useCallback(() => {
    longPressOccurredRef.current = true;
    setShouldBlockEvents(true);
  }, []);

  // wrap callback to prevent events and trigger blocking
  const wrappedCallback = useMemo<LongPressCallback | null>(() => {
    if (!callback) return null;

    return (event, _meta) => {
      event.preventDefault();
      event.stopPropagation();
      setBlockingFlags();
      callback();
    };
  }, [callback, setBlockingFlags]);

  const handleStart = useCallback<LongPressCallback>(
    (event, meta) => {
      resetFlags();
      options?.onStart?.(event, meta);
    },
    [options, resetFlags],
  );

  const handleFinish = useCallback<LongPressCallback>(
    (event, meta) => {
      // prevent events to stop browser from queuing click
      event.preventDefault();
      event.stopPropagation();
      setBlockingFlags();
      options?.onFinish?.(event, meta);
    },
    [options, setBlockingFlags],
  );

  const handleCancel = useCallback<LongPressCallback>(
    (event, meta) => {
      resetFlags();
      options?.onCancel?.(event, meta);
    },
    [options, resetFlags],
  );

  const longPressHandlers = useBaseLongPress(wrappedCallback, {
    ...options,
    captureEvent: true,
    onStart: handleStart,
    onFinish: handleFinish,
    onCancel: handleCancel,
  });

  // block events in capture phase to intercept before other listeners
  // blocking events is necessary because ContextualDialogs will fire their click away listeners upon receiving click event from the target element which will close the dialog
  useEffect(() => {
    if (!shouldBlockEvents) return;

    const blocker = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if ('stopImmediatePropagation' in e) {
        e.stopImmediatePropagation();
      }
    };

    // use capture phase to intercept events before they reach target listeners
    BLOCKED_EVENTS.forEach((event) => {
      document.addEventListener(event, blocker, { capture: true, once: true });
    });

    // reset flags after blocking period to allow normal events again
    const timeoutId = setTimeout(() => {
      resetFlags();
    }, BLOCKED_EVENTS_DURATION_MS);

    return () => {
      BLOCKED_EVENTS.forEach((event) => {
        document.removeEventListener(event, blocker, { capture: true });
      });
      clearTimeout(timeoutId);
    };
  }, [shouldBlockEvents, resetFlags]);

  const longPressProps = useMemo(() => longPressHandlers(), [longPressHandlers]);

  return {
    longPressOccurredRef,
    longPressProps,
  };
}
